import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  tenantApi,
  type OperationalPresenceResponse,
  type OperationalPresenceSlot,
  type OperationalPresenceUser,
  type PresenceIncidentDto,
  type DevicePresenceSummary,
} from '../../services/tenantApi';
import { formatDateTime } from '../../shared/dateTimeFormatting';
import { useDateTimePreferences } from '../../shared/DateTimeProvider';
import { ONCALL_PRESENCE_CHANGED_EVENT, PRESENCE_POLL_MS } from '../../shared/onCallPresenceEvents';
import { PresenceDot, formatAgo } from '../../components/PresenceDot';
import './OnCallPresencePanel.css';

type IncidentFilter = 'Open' | 'all';

const RESOLUTION_LABELS: Record<string, string> = {
  DeviceOnline: 'Device came online',
  GroupMemberLive: 'Group member online',
  ShiftEnded: 'Shift ended',
  NoLongerOnCall: 'No longer on call',
  PresenceDisabled: 'Presence checks disabled',
};

export const resolutionLabel = (reason?: string | null) => (reason ? RESOLUTION_LABELS[reason] || reason : '');

const when = (iso?: string | null) => (iso
  ? formatDateTime(iso, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
  : '—');

const ContactList: React.FC<{ users: OperationalPresenceUser[] }> = ({ users }) => (
  <div className="presence-contact-list">
    {users.map(u => (
      <span key={`${u.userId}-${u.dutyRole || ''}`} className="presence-slot-user">
        <PresenceDot presence={u.presence} />
        <span>{u.userName || 'Unknown user'}</span>
        {u.dutyRole === 'Escalation' && <span className="presence-chip">Escalation</span>}
      </span>
    ))}
  </div>
);

const SlotCell: React.FC<{ slot?: OperationalPresenceSlot | null; showStart?: boolean }> = ({ slot, showStart }) => {
  if (!slot || !slot.users?.length) return <span className="presence-muted">Unassigned</span>;
  return (
    <div className="presence-slot">
      <ContactList users={slot.users} />
      {slot.isOverride && <span className="presence-chip">Override</span>}
      {showStart && slot.startUtc && <div className="presence-muted">from {when(slot.startUtc)}</div>}
    </div>
  );
};

/**
 * Schedule-scoped operational contacts; management can also display assurance incidents.
 * Refreshes on the oncallPresenceChanged SignalR event with a 60 s polling fallback.
 */
export const OnCallPresencePanel: React.FC<{
  scheduleId?: string;
  incidentsOnly?: boolean;
  showIncidents?: boolean;
  compact?: boolean;
}> = ({ scheduleId, incidentsOnly = false, showIncidents = false, compact = false }) => {
  useDateTimePreferences();
  const [presence, setPresence] = useState<OperationalPresenceResponse | null>(null);
  const [incidents, setIncidents] = useState<PresenceIncidentDto[]>([]);
  const [filter, setFilter] = useState<IncidentFilter>('Open');
  const [userNames, setUserNames] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const canShowIncidents = incidentsOnly || showIncidents;

  useEffect(() => {
    if (!canShowIncidents) return;
    let active = true;
    // Best-effort name lookup for escalation contacts; TeamLeads without user-list access see a count instead.
    tenantApi.getUsers({ take: 500 })
      .then(result => {
        if (!active) return;
        const map = new Map<string, string>();
        for (const u of result?.users || []) {
          const id = String(u.userId || u.id || '').toLowerCase();
          if (id) map.set(id, u.displayName || u.email || id);
        }
        setUserNames(map);
      })
      .catch(() => undefined);
    return () => { active = false; };
  }, [canShowIncidents]);

  const load = useCallback(async (isCurrent: () => boolean) => {
    try {
      const [presenceResult, incidentResult] = await Promise.all([
        incidentsOnly ? Promise.resolve(null) : tenantApi.getOperationalPresence(scheduleId),
        canShowIncidents ? tenantApi.getPresenceIncidents(filter, 50) : Promise.resolve([]),
      ]);
      if (!isCurrent()) return;
      setPresence(presenceResult);
      setIncidents(incidentResult);
      setError(null);
    } catch (err) {
      if (isCurrent()) {
        setPresence(null);
        setIncidents([]);
        setError(err instanceof Error ? err.message : 'Failed to load presence');
      }
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [filter, scheduleId, incidentsOnly, canShowIncidents]);

  useEffect(() => {
    let active = true;
    let revision = 0;
    setPresence(null);
    setIncidents([]);
    setError(null);
    setLoading(true);
    const refresh = () => {
      const request = ++revision;
      void load(() => active && request === revision);
    };
    refresh();
    const timer = window.setInterval(refresh, PRESENCE_POLL_MS);
    window.addEventListener(ONCALL_PRESENCE_CHANGED_EVENT, refresh);
    window.addEventListener('nog:schedules:changed', refresh);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener(ONCALL_PRESENCE_CHANGED_EVENT, refresh);
      window.removeEventListener('nog:schedules:changed', refresh);
    };
  }, [load]);

  const presenceByUser = useMemo(() => {
    const map = new Map<string, DevicePresenceSummary>();
    for (const schedule of presence?.schedules || []) {
      for (const slot of [schedule.current, schedule.next]) {
        for (const u of slot?.users || []) map.set(String(u.userId).toLowerCase(), u.presence);
      }
    }
    return map;
  }, [presence]);

  const escalatedTo = (ids: string[]) => {
    if (!ids?.length) return '—';
    const names = ids.map(id => userNames.get(String(id).toLowerCase())).filter(Boolean) as string[];
    return names.length === ids.length ? names.join(', ') : `${ids.length} contact${ids.length === 1 ? '' : 's'}`;
  };

  const schedules = (presence?.schedules || []).slice().sort((a, b) => (a.scheduleName || '').localeCompare(b.scheduleName || ''));
  const now = Date.now();

  return (
    <section className={`oncall-presence-panel${compact ? ' oncall-presence-compact' : ''}`} aria-busy={loading}>
      {!incidentsOnly && <>
      <header className="oncall-presence-header">
        <h3>{scheduleId ? 'Schedule reachability' : 'Operational reachability'}</h3>
        <span className="presence-legend">
          <span><PresenceDot presence={{ state: 'Live' }} /> Live</span>
          <span><PresenceDot presence={{ state: 'Stale' }} /> Stale</span>
          <span><PresenceDot presence={{ state: 'None' }} /> No device</span>
        </span>
      </header>
      <p className="presence-muted presence-explanation">Mobile-device heartbeat, not web login or guaranteed availability. Leaders are contacts, not automatically on duty.</p>

      {schedules.length > 0 && (
        <div className="presence-table-scroll"><table className="data-table presence-table">
          <thead>
            <tr><th>Schedule</th><th>On call now</th><th>Next on call</th><th>Team leaders</th></tr>
          </thead>
          <tbody>
            {schedules.map(s => (
              <tr key={s.scheduleId} data-schedule-id={s.scheduleId}>
                <td>{s.scheduleName}<div className="presence-muted">{s.scheduleType}</div></td>
                <td><SlotCell slot={s.current} /></td>
                <td><SlotCell slot={s.next} showStart /></td>
                <td>{s.leaders?.length ? <ContactList users={s.leaders} /> : <span className="presence-muted">No leaders configured</span>}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
      {!loading && !error && schedules.length === 0 && <p className="presence-muted presence-empty">No active schedules available in your operational scope.</p>}
      </>}
      {error && <div className="presence-error" role="alert">{error}</div>}
      {canShowIncidents && <details className="presence-incident-details">
        <summary>Presence incidents{!loading && !error ? ` (${incidents.length}${incidents.length === 50 ? '+' : ''})` : ''}</summary>

      <div className="oncall-presence-subheader">
        <h4>{filter === 'Open' ? 'Open presence incidents' : 'Recent presence incidents'}</h4>
        <label>
          Show{' '}
          <select value={filter} onChange={e => setFilter(e.target.value as IncidentFilter)} aria-label="Incident filter">
            <option value="Open">Open</option>
            <option value="all">All recent</option>
          </select>
        </label>
      </div>
      {!loading && !error && incidents.length === 0 ? (
        <p className="presence-muted presence-empty">
          {filter === 'Open' ? 'No open presence incidents recorded. This does not confirm device reachability.' : 'No presence incidents recorded.'}
        </p>
      ) : (
        <div className="presence-table-scroll"><table className="data-table presence-table presence-incidents">
          <thead>
            <tr>
              <th>User</th><th>Schedule</th><th>Shift start</th><th>Detected</th><th>Notices</th>
              <th>Escalated to</th><th>Last seen</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {incidents.map(i => {
              const userPresence = presenceByUser.get(String(i.userId).toLowerCase());
              return (
                <tr key={i.incidentId} data-incident-id={i.incidentId} className={`presence-incident-${String(i.status).toLowerCase()}`}>
                  <td>
                    <PresenceDot presence={userPresence ?? null} />
                    {i.userName || userNames.get(String(i.userId).toLowerCase()) || 'Unknown user'}
                  </td>
                  <td>{i.scheduleName || '—'}</td>
                  <td>{when(i.shiftStartUtc)}</td>
                  <td>{when(i.detectedAtUtc)}</td>
                  <td>{i.userNoticeCount}</td>
                  <td>{i.escalatedAtUtc ? escalatedTo(i.escalatedToUserIds) : 'Not yet'}</td>
                  <td>{formatAgo(userPresence?.lastSeenAtUtc, now) || (userPresence ? 'never' : '—')}</td>
                  <td>
                    <span className={`presence-status presence-status-${String(i.status).toLowerCase()}`}>{i.status}</span>
                    {i.resolutionReason && <div className="presence-muted">{resolutionLabel(i.resolutionReason)}</div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
      )}
      </details>}
    </section>
  );
};

export default OnCallPresencePanel;
